import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";
import { FormFeedback, FormGroup, Input, Label } from "reactstrap";
import Select from "react-select";
import SyncSelect from "react-select";
import {
  algorithmHierarki,
  CHOOSE_PENGGUNAAN_TANAH,
  STATUS_HAK,
  uppercaseFirstLetter,
  HIERARKI_CASE_NAME,
  PEMANFAATAN_TANAH,
  isMultiValueToString,
  KOMODITAS,
  replaceAll,
} from "~/utils";
import InputImages from "~/components/InputImages";
import request from "~/utils/request";

const UploadBidang = () => {
  const {
    handleSubmit,
    register,
    errors,
    watch,
    control,
    setValue,
    reset: resetForm,
  } = useForm({
    mode: "onChange",
  });

  const { desa } = useSelector((state) => state.Auth);
  const { activeList } = useSelector((state) => state.Map);

  const watchImageDepan = watch("gambar_depan", null);
  const watchPenggunaanTanah = watch("penggunaan_tanah", null);
  const watchPemanfaatanTanah = watch("pemanfaatan_tanah", null);
  const watchKomoditas = watch("komoditas", null);

  const [loading, setLoading] = useState(false);
  const [hierarkiSig, setHierarkiSig] = useState(null);

  const watchSubDesa = watch("subdesa", null);
  const watchSubSubDesa = watch("subsubdesa", null);
  const watchRW = watch("rw", null);

  const subdesa = desa?.hierarki?.subdesa;
  const subsubdesa = desa?.hierarki?.subsubdesa;
  const RW = desa?.hierarki?.rw;
  const RT = desa?.hierarki?.rt;

  const hirarkiRT = RW
    ? watchRW && watchRW?.value !== "semua" && RT
    : subsubdesa
    ? watchSubDesa && watchSubSubDesa?.value !== "semua"
      ? watchSubSubDesa
      : false
    : watchSubDesa;

  const hirarkiRW = RW
    ? subsubdesa
      ? watchSubSubDesa?.value !== "semua"
        ? watchSubSubDesa
        : false
      : watchSubDesa?.value !== "semua"
      ? watchSubDesa
      : false
    : RW;
  const hierarkiSubSubDesa = subsubdesa
    ? watchSubDesa?.value !== "semua"
      ? watchSubDesa
      : false
    : subsubdesa;

  const getHierarkiName = algorithmHierarki(desa?.hierarki);

  const getDynamicHierarkiSig = (filter, target) => {
    if (filter) {
      request.get(`/v1/sighierarki?${filter}&target=${target}`).then((res) => {
        setHierarkiSig((prevHiearkiSig) => ({
          ...prevHiearkiSig,
          [target]: res.data.data.map((hierarki) => ({
            label: uppercaseFirstLetter(hierarki),
            value: hierarki,
          })),
        }));
      });
    } else {
      request.get(`/v1/sighierarki?target=${target}`).then((res) => {
        setHierarkiSig((prevHiearkiSig) => ({
          ...prevHiearkiSig,
          [target]: res.data.data.map((hierarki) => ({
            label: uppercaseFirstLetter(hierarki),
            value: hierarki,
          })),
        }));
      });
    }
  };

  // const isFeature = activeList?.feature?.properties;
  const isProperties = activeList?.feature?.properties?.properties;

  const handleUploadBidang = (values) => {
    const keterangan_pemanfaatan_tanah =
      values?.keterangan_pemanfaatan_tanah?.value ??
      replaceAll(
        values?.keterangan_pemanfaatan_tanah?.toLowerCase(),
        " ",
        "_"
      ) ??
      "";

    const komoditas =
      replaceAll(values?.komoditas_lainnya?.toLowerCase(), " ", "_") ??
      values?.komoditas?.value ??
      "";

    let datas = {
      komoditas,
      keterangan_pemanfaatan_tanah,
      status_hak: values?.status_hak?.value ?? "-",
      penggunaan_tanah: values?.penggunaan_tanah?.value ?? "",
      subdesa: values?.subdesa?.value ?? "",
      pemanfaatan_tanah: isMultiValueToString(
        values?.pemanfaatan_tanah,
        "value",
        ","
      ),
      properties: {
        ...isProperties,
      },
    };

    if (values.nomor_hak) {
      datas = {
        ...datas,
        nomor_hak: values?.nomor_hak,
      };
    }

    if (typeof watchImageDepan !== "string" && watchImageDepan) {
      datas = {
        ...datas,
        gambar_depan: watchImageDepan,
      };
    }

    if (subsubdesa) {
      datas = {
        ...datas,
        subsubdesa: values?.subsubdesa?.value ?? "-",
        properties: {
          ...datas.properties,
          subsubdesa: values?.subsubdesa?.value ?? "-",
        },
      };
    }

    if (RW) {
      datas = {
        ...datas,
        rw: values?.rw?.value ?? "-",
        properties: {
          ...datas.properties,
          rw: values?.rw?.value ?? "-",
        },
      };
    }

    if (RT) {
      datas = {
        ...datas,
        rt: values?.rt?.value ?? "-",
        properties: {
          ...datas.properties,
          rt: values?.rt?.value ?? "-",
        },
      };
    }

    datas = {
      ...datas,
      properties: JSON.stringify({
        ...datas.properties,
      }),
    };

    setLoading(true);

    const formData = new FormData();
    const id = activeList?.feature?.properties?.id;

    Object.entries(datas).forEach(([key, value]) =>
      formData.append(key, value)
    );

    request({
      url: `/v1/sig/${id}/`,
      method: "patch",
      data: formData,
    })
      .then(() => toast.success("Data bidang berhasil diperbaharui !"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const featureLayer = activeList?.feature?.properties;
    const isKomoditasLainnya = KOMODITAS.some(
      (el) => el.value === featureLayer?.komoditas
    );

    resetForm();

    setTimeout(() => {
      if (featureLayer?.penggunaan_tanah) {
        setValue("penggunaan_tanah", {
          label: uppercaseFirstLetter(featureLayer?.penggunaan_tanah),
          value: featureLayer?.penggunaan_tanah,
        });
      }

      if (featureLayer?.pemanfaatan_tanah) {
        const pemanfaatanTanahMapper = featureLayer?.pemanfaatan_tanah
          ?.split(",")
          ?.map((pmnfth) => ({
            label: uppercaseFirstLetter(pmnfth),
            value: pmnfth,
          }));

        setValue("pemanfaatan_tanah", pemanfaatanTanahMapper);
      }

      if (featureLayer?.status_hak) {
        setValue("status_hak", {
          label: uppercaseFirstLetter(featureLayer?.status_hak),
          value: featureLayer?.status_hak,
        });
      }

      if (featureLayer?.nomor_hak) {
        setValue("nomor_hak", featureLayer?.nomor_hak);
      }

      setValue("subdesa", {
        label: featureLayer?.subdesa,
        value: featureLayer?.subdesa,
      });
      setValue("subsubdesa", {
        label: featureLayer?.subsubdesa,
        value: featureLayer?.subsubdesa,
      });
      setValue("rw", { label: featureLayer?.rw, value: featureLayer?.rw });
      setValue("rt", { label: featureLayer?.rt, value: featureLayer?.rt });

      if (
        featureLayer?.penggunaan_tanah === CHOOSE_PENGGUNAAN_TANAH.PERTANIAN
      ) {
        setValue("keterangan_pemanfaatan_tanah", {
          label: uppercaseFirstLetter(
            featureLayer?.keterangan_pemanfaatan_tanah
          ),
          value: featureLayer?.keterangan_pemanfaatan_tanah,
        });
      } else {
        setValue(
          "keterangan_pemanfaatan_tanah",
          featureLayer?.keterangan_pemanfaatan_tanah
        );
      }

      if (!isKomoditasLainnya) {
        setValue("komoditas", { label: "Lainnya", value: "lainnya" });
        setValue("komoditas_lainnya", featureLayer?.komoditas);
      } else {
        setValue(
          "komoditas",
          KOMODITAS.find((elem) => elem.value === featureLayer?.komoditas)
        );
      }
    }, 300);
  }, [activeList]);

  useEffect(() => {
    getDynamicHierarkiSig(null, "subdesa");
  }, []);

  useEffect(() => {
    const subDesaTosubSubDesa = [
      HIERARKI_CASE_NAME.ALL,
      HIERARKI_CASE_NAME.SUBDESA_SUBSUBDESA_RW,
      HIERARKI_CASE_NAME.SUBDESA_SUBSUBDESA_RT,
      HIERARKI_CASE_NAME.SUBDESA_SUBSUBDESA,
    ];

    const subDesaToRW = [
      HIERARKI_CASE_NAME.SUBDESA_RW_RT,
      HIERARKI_CASE_NAME.SUBDESA_RW,
    ];

    if (subDesaTosubSubDesa.includes(getHierarkiName)) {
      if (watchSubDesa) {
        if (watchSubDesa?.value !== "semua") {
          getDynamicHierarkiSig(
            `filter{subdesa}=${watchSubDesa?.value}`,
            "subsubdesa"
          );
        }
      }
    }

    if (subDesaToRW.includes(getHierarkiName)) {
      if (watchSubDesa) {
        if (watchSubDesa?.value !== "semua") {
          getDynamicHierarkiSig(`filter{subdesa}=${watchSubDesa?.value}`, "rw");
        }
      }
    }

    if (getHierarkiName === HIERARKI_CASE_NAME.SUBDESA_RT) {
      if (watchSubDesa) {
        if (watchSubDesa?.value !== "semua") {
          getDynamicHierarkiSig(`filter{subdesa}=${watchSubDesa?.value}`, "rt");
        }
      }
    }
  }, [watchSubDesa]);

  useEffect(() => {
    const subSubDesaToRW = [
      HIERARKI_CASE_NAME.ALL,
      HIERARKI_CASE_NAME.SUBDESA_SUBSUBDESA_RW,
    ];

    if (subSubDesaToRW.includes(getHierarkiName)) {
      if (watchSubSubDesa) {
        if (watchSubSubDesa?.value !== "semua") {
          getDynamicHierarkiSig(
            `filter{subdesa}=${watchSubDesa?.value}&filter{subsubdesa}=${watchSubSubDesa?.value}`,
            "rw"
          );
        }
      }
    }

    if (getHierarkiName === HIERARKI_CASE_NAME.SUBDESA_SUBSUBDESA_RT) {
      if (watchSubSubDesa) {
        if (watchSubSubDesa?.value !== "semua") {
          getDynamicHierarkiSig(
            `filter{subdesa}=${watchSubDesa?.value}&filter{subsubdesa}=${watchSubSubDesa?.value}`,
            "rt"
          );
        }
      }
    }
  }, [watchSubSubDesa]);

  useEffect(() => {
    const rwToRt = [HIERARKI_CASE_NAME.ALL, HIERARKI_CASE_NAME.SUBDESA_RW_RT];

    if (rwToRt.includes(getHierarkiName)) {
      if (watchRW?.value !== "semua") {
        getDynamicHierarkiSig(
          `filter{subdesa}=${watchSubDesa?.value}&filter{subsubdesa}=${watchSubSubDesa?.value}&filter{rw}=${watchRW?.value}`,
          "rt"
        );
      }
    }

    if (getHierarkiName === HIERARKI_CASE_NAME.SUBDESA_RW_RT) {
      if (watchRW) {
        if (watchRW?.value !== "semua") {
          getDynamicHierarkiSig(
            `filter{subdesa}=${watchSubDesa?.value}&filter{rw}=${watchRW?.value}`,
            "rt"
          );
        }
      }
    }
  }, [watchRW]);

  const keySelectSubSubDesa = !!watchSubDesa?.value
    ? watchSubDesa?.value
    : null;

  const keySelectRW = subsubdesa
    ? !!watchSubSubDesa?.value
      ? watchSubSubDesa?.value
      : null
    : !!watchSubDesa?.value
    ? watchSubDesa?.value
    : null;

  const keySelectRT = RW
    ? !!watchRW?.value
      ? watchRW?.value
      : null
    : subsubdesa
    ? !!watchSubSubDesa?.value
      ? watchSubSubDesa?.value
      : watchSubDesa?.value
    : null;

  const isShowGambarDepan =
    watchPenggunaanTanah?.value === CHOOSE_PENGGUNAAN_TANAH.NON_PERTANIAN ||
    watchPenggunaanTanah?.value === CHOOSE_PENGGUNAAN_TANAH.CAMPURAN;

  const isKomoditasSelect =
    watchPenggunaanTanah?.value === CHOOSE_PENGGUNAAN_TANAH.PERTANIAN ||
    watchPenggunaanTanah?.value === CHOOSE_PENGGUNAAN_TANAH.CAMPURAN;

  return (
    <form
      autoComplete="off"
      className="p-2"
      onSubmit={handleSubmit(handleUploadBidang)}
    >
      <div className="pb-5 mb-5" style={{ height: "450px", overflow: "auto" }}>
        <FormGroup>
          <Label>NBT</Label>
          <Input
            name="nama"
            readOnly
            innerRef={register}
            value={isProperties?.nama}
          />
        </FormGroup>
        <FormGroup>
          <Label>Luas</Label>
          <Input
            readOnly
            type="text"
            name="luas"
            innerRef={register}
            value={
              isProperties?.Luas || isProperties?.luas
                ? `${
                    isProperties?.Luas?.toFixed(2) ||
                    isProperties?.luas?.toFixed(2)
                  } m²`
                : "-"
            }
          />
        </FormGroup>
        <FormGroup>
          <Label>Penggunaan Tanah</Label>
          <Controller
            control={control}
            name="penggunaan_tanah"
            render={({ onChange, value }) => (
              <Select
                value={value}
                onChange={(val) => onChange(val)}
                placeholder="Pilih Penggunaan Tanah"
                options={CHOOSE_PENGGUNAAN_TANAH.SELECT}
              />
            )}
          />
          {errors?.penggunaan_tanah && (
            <FormFeedback className="d-block">
              Masukkan penggunaan tanah
            </FormFeedback>
          )}
        </FormGroup>

        {isShowGambarDepan && (
          <FormGroup>
            <Label>Gambar Tampak Depan</Label>
            <InputImages
              name="gambar_depan"
              control={control}
              setValue={setValue}
              buttonText="Ambil Gambar"
              defaultValue={activeList?.feature?.properties?.gambar_depan}
              fileSizeError="Gambar ini terlalu besar, coba gambar lain."
            />
          </FormGroup>
        )}

        {watchPenggunaanTanah?.value && (
          <FormGroup>
            <Label>Pemanfaatan Tanah</Label>
            <Controller
              control={control}
              name="pemanfaatan_tanah"
              key={
                !!watchPenggunaanTanah?.value
                  ? watchPenggunaanTanah?.value
                  : null
              }
              render={({ onChange, value }) => {
                // const checkIsMulti =
                //   watchPenggunaanTanah?.value ===
                //   CHOOSE_PENGGUNAAN_TANAH.CAMPURAN;
                const optionsPemanfaatanTanah =
                  watchPenggunaanTanah?.value ===
                  CHOOSE_PENGGUNAAN_TANAH.CAMPURAN
                    ? PEMANFAATAN_TANAH.pertanian.concat(
                        PEMANFAATAN_TANAH["non pertanian"]
                      )
                    : PEMANFAATAN_TANAH?.[
                        watchPenggunaanTanah?.value?.toLowerCase()
                      ];
                return (
                  <Select
                    isMulti
                    value={value}
                    onChange={(val) => onChange(val)}
                    placeholder="Pilih Pemanfaatan Tanah"
                    options={optionsPemanfaatanTanah.map((el) => ({
                      label: el.label,
                      value: el.label,
                    }))}
                  />
                );
              }}
            />

            {errors?.pemanfaatan_tanah && (
              <FormFeedback className="d-block">
                Pilih pemanfaatan tanah
              </FormFeedback>
            )}
          </FormGroup>
        )}

        {watchPemanfaatanTanah &&
          (isKomoditasSelect ? (
            <FormGroup>
              <Label>Komoditas</Label>

              <Controller
                name="komoditas"
                control={control}
                render={({ onChange, value }) => (
                  <Select
                    value={value}
                    options={KOMODITAS}
                    placeholder="Pilih Komoditas"
                    onChange={(val) => onChange(val)}
                  />
                )}
              />
            </FormGroup>
          ) : (
            <FormGroup>
              <Label>Keterangan</Label>

              <Input innerRef={register} name="keterangan_pemanfaatan_tanah" />
            </FormGroup>
          ))}

        {watchKomoditas?.value === "lainnya" && (
          <FormGroup>
            <Label>Komoditas Lainnya</Label>

            <Input
              innerRef={register}
              placeholder="Masukkan Komoditas Lainnya"
              name="komoditas_lainnya"
            />
          </FormGroup>
        )}

        <FormGroup>
          <Label>Status Hak</Label>

          <Controller
            name="status_hak"
            control={control}
            render={({ onChange, value }) => (
              <Select
                value={value}
                options={STATUS_HAK.LIST}
                placeholder="Pilih Status Hak"
                onChange={(val) => onChange(val)}
              />
            )}
          />

          {errors?.status_hak && (
            <FormFeedback className="d-block">Pilih Status Hak</FormFeedback>
          )}
        </FormGroup>

        <FormGroup>
          <Label>Nomor Hak</Label>
          <Input
            type="number"
            name="nomor_hak"
            innerRef={register}
            placeholder="Masukkan Nomor Hak"
            defaultValue={activeList?.feature?.properties?.nomor_hak}
          />
        </FormGroup>

        {subdesa && (
          <FormGroup>
            <Label>{uppercaseFirstLetter(desa?.hierarki?.jenis_subdesa)}</Label>
            <Controller
              name="subdesa"
              control={control}
              rules={{ required: true }}
              defaultValue={null}
              render={({ onChange, value }) => {
                return (
                  <SyncSelect
                    value={value}
                    options={hierarkiSig?.subdesa}
                    onChange={(val) => onChange(val)}
                    styles={{
                      // Fixes the overlapping problem of the component
                      menu: (provided) => ({ ...provided, zIndex: 9999 }),
                    }}
                  />
                );
              }}
            />
            {errors?.subdesa && (
              <FormFeedback className="d-block">
                Pilih {uppercaseFirstLetter(desa?.hierarki?.jenis_subdesa)}
              </FormFeedback>
            )}
          </FormGroup>
        )}

        {hierarkiSubSubDesa && (
          <FormGroup>
            <Label>
              {uppercaseFirstLetter(desa?.hierarki?.jenis_subsubdesa)}
            </Label>
            <Controller
              name="subsubdesa"
              control={control}
              defaultValue={null}
              rules={{ required: true }}
              key={keySelectSubSubDesa}
              render={({ onChange, value }) => {
                return (
                  <SyncSelect
                    value={value}
                    options={hierarkiSig?.subsubdesa}
                    onChange={(val) => onChange(val)}
                    styles={{
                      // Fixes the overlapping problem of the component
                      menu: (provided) => ({ ...provided, zIndex: 9999 }),
                    }}
                  />
                );
              }}
            />
            {errors?.subsubdesa && (
              <FormFeedback className="d-block">
                Pilih {uppercaseFirstLetter(desa?.hierarki?.jenis_subsubdesa)}
              </FormFeedback>
            )}
          </FormGroup>
        )}

        {hirarkiRW && (
          <FormGroup className="position-relative mb-2">
            <Label>RW</Label>
            <Controller
              name="rw"
              placeholder="Pilih atau cari rw"
              defaultValue={null}
              key={keySelectRW}
              control={control}
              rules={{ required: true }}
              render={({ onChange, value }) => {
                return (
                  <SyncSelect
                    value={value}
                    options={hierarkiSig?.rw}
                    onChange={(val) => onChange(val)}
                    styles={{
                      // Fixes the overlapping problem of the component
                      menu: (provided) => ({ ...provided, zIndex: 9999 }),
                    }}
                  />
                );
              }}
            />
            {errors?.rw && (
              <FormFeedback className="d-block">Pilih RW</FormFeedback>
            )}
          </FormGroup>
        )}

        {hirarkiRT && (
          <FormGroup className="position-relative mb-2">
            <Label>RT</Label>
            <Controller
              name="rt"
              placeholder="Pilih atau cari rt"
              control={control}
              key={keySelectRT}
              defaultValue={null}
              rules={{ required: true }}
              render={({ onChange, value }) => {
                return (
                  <SyncSelect
                    value={value}
                    options={hierarkiSig?.rt}
                    onChange={(val) => onChange(val)}
                    styles={{
                      // Fixes the overlapping problem of the component
                      menu: (provided) => ({ ...provided, zIndex: 9999 }),
                    }}
                  />
                );
              }}
            />

            {errors?.rt && (
              <FormFeedback className="d-block">Pilih RT</FormFeedback>
            )}
          </FormGroup>
        )}
      </div>
      <div
        className="simpanPerubahan"
        style={{ position: "absolute", left: 0, right: 0, bottom: -55 }}
      >
        <button
          type="submit"
          disabled={loading}
          className="btn btn-primary"
          style={{
            textTransform: "uppercase",
            position: "absolute",
            left: "0",
            right: 0,
            bottom: "0",
            display: "block",
            width: "100%",
            height: "50px",
            fontSize: "1rem",
          }}
        >
          {loading ? "memperbaharui data..." : "Simpan"}
        </button>
      </div>
    </form>
  );
};

export default UploadBidang;
